import type { DatabaseDriver } from '@w/db'
import { crdt } from '@w/crdt'

type Direction = 'outgoing' | 'incoming'
type StagedFrame = { userId: string; direction: Direction; frame: Uint8Array }
type StoredFrame = StagedFrame & { digest: string; epoch: number; ranges: string }

/** Device-local recovery, deliberately independent of the editor's live version. */
export function createLiveStaging(driver: DatabaseDriver) {
  return {
    async stage(pageId: string, userId: string, direction: Direction, input: Uint8Array) {
      const frame = new Uint8Array(input)
      const metadata = crdt.inspectUpdate(frame)
      if (!userId || metadata.blocks.length === 0 || metadata.blocks.some(block => block.user !== userId)) {
        throw new Error('Live frame author mismatch')
      }
      const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', frame)), byte => byte.toString(16).padStart(2, '0')).join('')
      const ranges = metadata.blocks.map(block => [block.client, block.start + block.length] as const)
      await driver.exec('INSERT OR IGNORE INTO pageLiveEdits(pageId,userId,direction,digest,epoch,ranges,frame) VALUES(?,?,?,?,?,?,?)',
        [pageId, userId, direction, digest, metadata.epoch, JSON.stringify(ranges), frame])
    },
    read(pageId: string): Promise<StagedFrame[]> {
      return driver.query<StagedFrame>('SELECT userId,direction,frame FROM pageLiveEdits WHERE pageId=? ORDER BY rowid', [pageId])
    },
    /** Only integrated native page clocks may retire recovery frames. Never pass a live editor version. */
    retireCovered(pageId: string) {
      return driver.transaction(async tx => {
        const [page] = await tx.query<{ data: Uint8Array }>('SELECT data FROM pages WHERE id=?', [pageId])
        if (!page) return
        const persisted = crdt.load(page.data)
        let version
        try { version = persisted.version() } finally { persisted.free() }
        const staged = await tx.query<StoredFrame>('SELECT userId,direction,digest,epoch,ranges FROM pageLiveEdits WHERE pageId=?', [pageId])
        for (const row of staged) {
          const ranges = JSON.parse(row.ranges) as [string, number][]
          if (row.epoch !== version.epoch || !ranges.every(([client, end]) => Number.isSafeInteger(Number(client)) && String(Number(client)) === client && (version.vector.get(Number(client)) ?? 0) >= end)) continue
          await tx.exec('DELETE FROM pageLiveEdits WHERE pageId=? AND userId=? AND direction=? AND digest=?', [pageId, row.userId, row.direction, row.digest])
        }
      })
    },
  }
}
